import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { LanguageContext } from "../../../constant/LanguageContext";
import { apiFetch } from "../../../context/AuthContext";
import { getTrimmedCompanyContext, encodePathSegment } from "../utils/encodeUtils";
import { translations } from "../constants/translations";
import { usePermissionLayers, PAGE_STATES, parseJsonOrThrow } from "./usePermissionLayers";

const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api`;

// re-export for existing importers (PreviewPanel, SegmentedControl, …)
export { PAGE_STATES };

const PERSONAL_LEVEL_PREFIX = "__user__";

/**
 * useUserPermissionConfig — the per-user 3-tier permission editor.
 *
 * The layer state + instant-save mutators live in usePermissionLayers; this
 * hook adds what is user-specific: the users list, selection, lazy creation
 * of the personal (__user__N) level, and unassignment.
 */
export const useUserPermissionConfig = (selectedCompany) => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  // ── registry (fetched once) ──
  const [registry, setRegistry] = useState(null);
  const [registryLoading, setRegistryLoading] = useState(true);

  // ── company users + which level each already has ──
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  // userId -> { levelName, isPersonal }
  const [userLevelMap, setUserLevelMap] = useState({});

  // ── selected user + their personal level ──
  const [selectedUser, setSelectedUser] = useState(null);
  const [levelLoading, setLevelLoading] = useState(false);
  const [personalLevel, setPersonalLevel] = useState(null); // { id, name, ... }

  // guards against responses landing after the admin switched users
  const activeUserRef = useRef(null);

  // ── low-level: POST /for-user (get-or-create + assign personal level) ──
  const createPersonalLevel = useCallback(async (user) => {
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    if (!client || !company) throw new Error("No company selected");
    const res = await apiFetch(`${API_BASE_URL}/admin-levels/for-user/${user.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientName: client, workingCompany: company }),
    });
    return parseJsonOrThrow(res);
  }, [selectedCompany.client, selectedCompany.company]);

  // forward declaration — ensurePersonalLevel is defined below but the layers
  // hook needs a stable resolver reference now
  const ensureRef = useRef(null);

  const layers = usePermissionLayers({
    selectedCompany,
    resolveLevelId: useCallback(() => ensureRef.current(), []),
    hasTarget: !!selectedUser?.id,
  });
  const { ingestLevel, resetLayers, absorbModuleIds, setError, setSuccess } = layers;

  // ── registry: fetch once ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setRegistryLoading(true);
        const res = await apiFetch(`${API_BASE_URL}/admin-levels/registry`, { method: "GET" });
        const data = await parseJsonOrThrow(res);
        if (!cancelled) setRegistry(data.registry || null);
      } catch (err) {
        if (!cancelled) setError(t.registryFailed);
      } finally {
        if (!cancelled) setRegistryLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // registry is static per session — intentionally no deps (t.registryFailed is stable enough for an error string)
    // eslint-disable-next-line
  }, []);

  // ── users list + existing-level badges ──
  const fetchUsers = useCallback(async (clientName, companyName) => {
    const { client, company } = getTrimmedCompanyContext(clientName, companyName);
    if (!client || !company) return;
    setUsersLoading(true);
    try {
      const query = new URLSearchParams({ clientName: client, workingCompany: company }).toString();
      const [usersRes, levelsRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/admin-levels/visibility/options/users?${query}`, { method: "GET" }),
        apiFetch(
          `${API_BASE_URL}/admin-levels/company/${encodePathSegment(client)}/${encodePathSegment(company)}?includePersonal=1`,
          { method: "GET" }
        ),
      ]);
      const usersData = await parseJsonOrThrow(usersRes);
      const levelsData = await levelsRes.json().catch(() => ({}));

      const map = {};
      (levelsData?.adminLevels || []).forEach((level) => {
        const isPersonal = String(level?.name || "").startsWith(PERSONAL_LEVEL_PREFIX);
        (level?.users || []).forEach((u) => {
          map[u.id] = { levelName: level.name, isPersonal };
        });
      });

      setUsers(usersData.users || []);
      setUserLevelMap(map);
      setUsersLoaded(true);
    } catch (err) {
      setUsers([]);
      setUserLevelMap({});
      setError(err.message || t.usersFailed);
    } finally {
      setUsersLoading(false);
    }
  }, [t.usersFailed, setError]);

  // ── pick a user ──
  // Users who already have a level (personal or inherited-shared) load/clone it
  // immediately. Users with NO level get an empty canvas only — their personal
  // level is created lazily on the first actual change, so merely clicking a
  // name never promotes anyone.
  const selectUser = useCallback(async (user) => {
    if (!user?.id) return;
    const { client, company } = getTrimmedCompanyContext(selectedCompany.client, selectedCompany.company);
    if (!client || !company) return;

    activeUserRef.current = user.id;
    setSelectedUser(user);
    setPersonalLevel(null);
    resetLayers();

    if (!userLevelMap[user.id]) return; // never configured — defer creation

    setLevelLoading(true);
    try {
      const data = await createPersonalLevel(user);
      if (activeUserRef.current !== user.id) return; // admin moved on
      setPersonalLevel(data.adminLevel || null);
      ingestLevel(data.adminLevel);
      setUserLevelMap((prev) => ({
        ...prev,
        [user.id]: { levelName: data.adminLevel?.name, isPersonal: true },
      }));
      if (data.created) setSuccess(t.personalLevelReady);
    } catch (err) {
      if (activeUserRef.current === user.id) {
        setSelectedUser(null);
        setError(err.message || t.personalLevelFailed);
      }
    } finally {
      if (activeUserRef.current === user.id) setLevelLoading(false);
    }
  }, [selectedCompany.client, selectedCompany.company, userLevelMap, createPersonalLevel, ingestLevel, resetLayers, setError, setSuccess, t.personalLevelReady, t.personalLevelFailed]);

  // resolve the personal level id, creating it on first use (deduped)
  const pendingCreateRef = useRef(null);
  const ensurePersonalLevel = useCallback(async () => {
    if (personalLevel?.id) return personalLevel.id;
    const user = selectedUser;
    if (!user?.id) throw new Error("No user selected");
    if (!pendingCreateRef.current) {
      pendingCreateRef.current = (async () => {
        const data = await createPersonalLevel(user);
        if (activeUserRef.current === user.id) {
          setPersonalLevel(data.adminLevel || null);
          // keep optimistic local state; just absorb any existing row ids
          absorbModuleIds(data.adminLevel);
          setUserLevelMap((prev) => ({
            ...prev,
            [user.id]: { levelName: data.adminLevel?.name, isPersonal: true },
          }));
        }
        return data.adminLevel?.id;
      })().finally(() => {
        pendingCreateRef.current = null;
      });
    }
    return pendingCreateRef.current;
  }, [personalLevel, selectedUser, createPersonalLevel, absorbModuleIds]);
  ensureRef.current = ensurePersonalLevel;

  // ── remove admin level (unassign only — the personal __user__ row is kept
  //     on purpose so re-promoting the user later restores their old config) ──
  const removeAdminLevel = useCallback(async () => {
    const user = selectedUser;
    if (!user?.id) return false;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/assign/${user.id}`, {
        method: "DELETE",
      });
      await parseJsonOrThrow(res);
      // drop the badge + reset to the empty canvas, but KEEP the user selected
      // so the admin can immediately re-configure them from scratch
      setUserLevelMap((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      if (activeUserRef.current === user.id) {
        setPersonalLevel(null);
        resetLayers();
      }
      setSuccess(t.levelRemoved);
      return true;
    } catch (err) {
      setError(err.message || t.levelRemoveFailed);
      return false;
    }
  }, [selectedUser, resetLayers, setError, setSuccess, t.levelRemoved, t.levelRemoveFailed]);

  const clearSelectedUser = useCallback(() => {
    activeUserRef.current = null;
    setSelectedUser(null);
    setPersonalLevel(null);
    resetLayers();
  }, [resetLayers]);

  return {
    // registry
    registry, registryLoading,
    // users panel
    users, usersLoading, usersLoaded, userLevelMap, fetchUsers,
    // selection + personal level
    selectedUser, selectUser, clearSelectedUser, personalLevel, levelLoading,
    removeAdminLevel, ensurePersonalLevel,
    // permission layers (read)
    pageStates: layers.pageStates,
    features: layers.features,
    hiddenColumns: layers.hiddenColumns,
    // permission layers (write — instant save, optimistic)
    setPageState: layers.setPageState,
    toggleFeature: layers.toggleFeature,
    toggleColumn: layers.toggleColumn,
    // toasts
    error: layers.error, success: layers.success, setError, setSuccess,
  };
};
